/**
 * Nexus Batch LLM Service — the propose_relationship behavior's model call in
 * the browser.
 *
 * The job, prompt, parse and switches are the shared core (one copy with the
 * server runner): supabase/functions/_shared/relationshipCore.ts. The
 * workspace prompt is an overlay on the built-in contract through the
 * instruction stack, never a replacement (SPEC-custom-agent-template.md §2 H).
 *
 * Cluster/variant detection is the Twins agent's job — Nexus never proposes
 * those types. Batch size: 25 items per call (pair space grows as n²).
 */

import {
  buildRelationshipPrompt,
  buildRelationshipSystemPrompt,
  parseRelationshipResponse,
  RELATIONSHIP_BATCH_SIZE,
  RELATIONSHIP_JOB,
  type RelationshipItemInput,
  type RelationshipReferenceDoc,
  type RelationshipResult,
} from '../../supabase/functions/_shared/relationshipCore.ts';
import { resolveModelParams } from '../../supabase/functions/_shared/estimateValueCore.ts';
import { AgentLlmError, callAgentLlm } from './llmClient';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

export const NEXUS_BATCH_SIZE = RELATIONSHIP_BATCH_SIZE;
export type NexusBatchItem = RelationshipItemInput;
export type NexusRelationshipResult = RelationshipResult;

export interface NexusBatchInput {
  items: NexusBatchItem[];
  model?: string;
  /** The workspace's prompt overlay — it specializes the contract, never replaces it. */
  systemPrompt?: string | null;
  modelParams?: Record<string, unknown> | null;
  referenceDocs?: RelationshipReferenceDoc[];
  timeoutMs?: number;
}

/** Parse a batch reply; throws when it is not a list of relationships. */
export function parseNexusBatchResults(raw: string, validIds?: Set<string>): NexusRelationshipResult[] {
  const parsed = parseRelationshipResponse(raw, validIds);
  if (!parsed) throw new AgentLlmError('Nexus batch agent could not parse the LLM response.');
  return parsed;
}

export async function runNexusBatchAgent(input: NexusBatchInput): Promise<NexusRelationshipResult[]> {
  if (input.items.length < 2) return [];

  const params = resolveModelParams(RELATIONSHIP_JOB.modelDefaults, input.modelParams ?? null);
  const { systemPrompt } = buildRelationshipSystemPrompt({ overlay: input.systemPrompt ?? null, language: params.language });
  const content = await callAgentLlm({
    userPrompt: buildRelationshipPrompt(input.items, input.referenceDocs ?? []),
    systemPrompt,
    model: input.model ?? DEFAULT_MODEL,
    conversationId: `nexus-batch-${Date.now()}`,
    maxTokens: params.maxTokens,
    temperature: params.temperature,
    timeoutMs: input.timeoutMs ?? 60_000,
  });

  return parseNexusBatchResults(content, new Set(input.items.map((i) => i.id)));
}
